import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, of } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { StageRequest } from '../../../shared/models/flow-manager-core';
import { Environment, SharedService } from '../store/shared-store/shared-storage.service';
import { map as _map, sortBy } from 'lodash';
import { FlowManagerService } from '../../../modules/flow/services/flow-manager.service';

@Injectable()
export class ManagementService {
  private static readonly baseUri: string =
    environment.controlBaseUrl + environment.managementBaseUri;
  private static readonly addClientUri = ManagementService.baseUri + '/client/add';
  private static readonly allChannelsUri = ManagementService.baseUri + '/channel/available';

  private entityCache$: Observable<Map<String, String>>;
  private envCache$: Observable<Array<string>>;
  private defaultChannelConfigCache$;
  private availableClientsCache$: Observable<Object>;
  private allChannelsCache$: Observable<Array<Object>>;
  private globalEnvironment: Environment;

  constructor(private http: HttpClient, private sharedService: SharedService, private flowManagerService: FlowManagerService) {}

  private retrieveEnv(): Observable<Array<string>> {
    return this.http.get<Array<string>>(
      this.createUri(['env', this.sharedService.getEntityType()])
    );
  }

  private retrieveEntities(): Observable<Map<String, String>> {
    if (this.sharedService.getEntityType() !== 'SYSTEM') {
      return this.http.get<Map<String, String>>(
          this.createUri([this.sharedService.getEnvironment(), this.sharedService.getEntityType()])
      );
    } else {
      const response = new Map<String, String>();
      return of(response.set('0', 'System'));
    }
  }

  getEntities(): Observable<Map<String, String>> {
    if (this.isReloadRequired()) {
      this.entityCache$ = this.retrieveEntities().pipe(
        map((entity) => {
          const newEntity = _map(entity, (value, key) => ({ key, value }));
          const sorted = sortBy(newEntity, (item) => item.value.toLowerCase());
          return sorted;
        }),
        tap((sortedMap) => {
          this.sharedService.setEntityList = sortedMap;
        }),
        shareReplay(1)
      );
    }

    return this.entityCache$;
  }

  getEnv(): Observable<Array<string>> {
    if (!this.envCache$) {
      this.envCache$ = this.retrieveEnv().pipe(shareReplay(1));
    }
    return this.envCache$;
  }

  private createUri(paths: string[]): string {
    let finalUrl = ManagementService.baseUri;
    paths.forEach((path) => {
      finalUrl = finalUrl.concat('/').concat(path);
    });
    return finalUrl;
  }

  public getDefaultChannelConfig() {
    if (!this.defaultChannelConfigCache$) {
      this.defaultChannelConfigCache$ = this.retrieveDefaultChannelConfig();
    }

    return this.defaultChannelConfigCache$;
  }

  private retrieveDefaultChannelConfig() {
    return this.http.get(this.createUri(['channel', 'defaultConfig']));
  }

  getAvailableEntities(): Observable<Object> {
    if (!this.availableClientsCache$) {
      this.availableClientsCache$ = this.retrieveAvailableEntities();
    }

    return this.availableClientsCache$;
  }

  private retrieveAvailableEntities() {
    return this.http.get(this.createUri(['client', 'available']));
  }

  public clearClientCache() {
    this.entityCache$ = null;
  }

  public addClient(stageRequest: StageRequest) {
    return this.http.post(ManagementService.addClientUri, stageRequest, { observe: 'response' });
  }

  public getAllChannels(): any {
    if (!this.allChannelsCache$) {
      this.allChannelsCache$ = this.retrieveAllChannels();
    }

    return this.allChannelsCache$;
  }

  private retrieveAllChannels(): any {
    return this.http.get(ManagementService.allChannelsUri);
  }

  private isReloadRequired(isReloadRequired: boolean = false): boolean {
    if (
      isReloadRequired ||
      JSON.stringify(this.globalEnvironment) !==
        JSON.stringify(this.sharedService.getGlobalEnvironment())
    ) {
      this.globalEnvironment = Object.assign({}, this.sharedService.getGlobalEnvironment());
      return true;
    }
    return false;
  }

  getEntityConfigs() {
    return this.http.get(this.flowManagerService.getEntityURI(['config']));
  }
}
